/**
 * WechatShare —— 微信分享 Unpoly 编译器组件
 *
 * hover 时动态从 CDN 加载 qrcode.js，
 * 生成当前页面 URL 的二维码并通过 Popover 展示。
 *
 * CDN: https://cdnjs.cloudflare.com/ajax/libs/qrcode/1.5.1/qrcode.min.js
 * 全局暴露 window.QRCode，仅首次 hover 时加载一次。
 */

/** qrcode CDN 地址 */
const QRCODE_CDN =
  "https://cdnjs.cloudflare.com/ajax/libs/qrcode/1.5.1/qrcode.min.js";

/** 单例 Promise，避免重复注入 script */
let loadPromise: Promise<void> | null = null;

/** 动态加载 qrcode CDN 脚本 */
function loadQRCodeLib(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if ((window as unknown as Record<string, unknown>).QRCode) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = QRCODE_CDN;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("qrcode CDN 加载失败"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * WechatShare —— 微信分享二维码 Popover
 *
 * 使用 Unpoly 编译器管理生命周期，替代 Alpine.js 实现。
 */
export class WechatShare {
  /** 二维码 dataURL 缓存 */
  private qrDataUrl = "";
  /** 是否正在生成 */
  private loading = false;
  /** 是否生成失败 */
  private error = false;

  /** hover 延迟关闭定时器 */
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  private btn: HTMLButtonElement;
  private popover: HTMLElement;
  private loadingEl: HTMLElement | null;
  private errorEl: HTMLElement | null;
  private qrImg: HTMLImageElement | null;

  constructor(container: HTMLElement) {
    this.btn = container.querySelector("[data-wechat-btn]")!;
    this.popover = container.querySelector("[data-wechat-popover]")!;
    this.loadingEl = this.popover.querySelector("[data-wechat-loading]");
    this.errorEl = this.popover.querySelector("[data-wechat-error]");
    this.qrImg = this.popover.querySelector("[data-wechat-qr]");

    this.init();
  }

  private init(): void {
    /* 按钮 hover 事件 */
    this.btn.addEventListener("mouseenter", this.handleShow);
    this.btn.addEventListener("mouseleave", this.handleHide);

    /* Popover 自身 hover 保持可见 */
    this.popover.addEventListener("mouseenter", this.handleShow);
    this.popover.addEventListener("mouseleave", this.handleHide);
  }

  /** 显示 Popover 并生成二维码 */
  private handleShow = async (): Promise<void> => {
    /* 清除延迟关闭 */
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    this.showPopover();

    /* 已生成则无需重复 */
    if (this.qrDataUrl) return;

    this.loading = true;
    this.error = false;
    this.render();

    try {
      await loadQRCodeLib();
      const qr = (window as unknown as Record<string, unknown>).QRCode as {
        toDataURL: (url: string, opts: object) => Promise<string>;
      };
      this.qrDataUrl = await qr.toDataURL(window.location.href, {
        width: 160,
        margin: 2,
        color: { dark: "#000000", light: "#ffffff" },
      });
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
      this.render();
    }
  };

  /** 延迟隐藏 Popover（给用户移入 Popover 的时间） */
  private handleHide = (): void => {
    this.hideTimer = setTimeout(() => {
      this.hidePopover();
    }, 120);
  };

  /** 显示 Popover（带过渡动画） */
  private showPopover(): void {
    this.popover.style.display = "";
    /* 使用 requestAnimationFrame 触发过渡动画，避免强制回流 (forced reflow) */
    requestAnimationFrame(() => {
      this.popover.classList.remove("opacity-0", "scale-95");
      this.popover.classList.add("opacity-100", "scale-100");
    });
  }

  /** 隐藏 Popover（带过渡动画） */
  private hidePopover(): void {
    this.popover.classList.remove("opacity-100", "scale-100");
    this.popover.classList.add("opacity-0", "scale-95");
    setTimeout(() => {
      /* 动画结束后隐藏 */
      if (this.popover.classList.contains("opacity-0")) {
        this.popover.style.display = "none";
      }
    }, 150);
  }

  /** 根据状态更新 DOM */
  private render(): void {
    /* 加载中 */
    if (this.loadingEl) {
      this.loadingEl.style.display = this.loading ? "" : "none";
    }
    /* 错误 */
    if (this.errorEl) {
      this.errorEl.style.display = this.error ? "" : "none";
    }
    /* 二维码图片 */
    if (this.qrImg) {
      if (this.qrDataUrl) {
        this.qrImg.src = this.qrDataUrl;
        this.qrImg.style.display = "";
      } else {
        this.qrImg.style.display = "none";
      }
    }
  }

  destroy(): void {
    this.btn.removeEventListener("mouseenter", this.handleShow);
    this.btn.removeEventListener("mouseleave", this.handleHide);
    this.popover.removeEventListener("mouseenter", this.handleShow);
    this.popover.removeEventListener("mouseleave", this.handleHide);
    if (this.hideTimer) clearTimeout(this.hideTimer);
  }

  /** 注册 Unpoly 编译器 */
  static compiler(selector: string): void {
    up.compiler(selector, (element: Element) => {
      const share = new WechatShare(element as HTMLElement);
      return () => share.destroy();
    });
  }
}
